import { Fragment, useCallback } from 'react';

import PropTypes from 'prop-types';
import { formatCostInCents, getCTR, getFormattedIntNumber } from 'utils/clickme';
import { VIEW_DATE_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import useTrls from 'hooks/translation/useTrls';

import TableCell, { TableCellKind } from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';

import { selectIsAveragePricePerClickAvailable } from 'store/models/statistics';
import { useSelector } from 'store/utils';

import 'components/Statistic/Item/Item.less';

const Item = ({ date, summary, data, showCr, showResponsesAndCr, isTotal = false, headers }) => {
    const isAveragePricePerClickAvailable = useSelector(selectIsAveragePricePerClickAvailable);

    const trl = useTrls();

    const getDataQa = useCallback((dataQa, dataQaT) => (!isTotal ? dataQa : dataQaT || ''), [isTotal]);

    return (
        <TableRow>
            <TableCell dataQa={getDataQa('statistic_date')}>
                {isTotal ? trl('statistic.Total') : formatDate(date, VIEW_DATE_FORMAT)}
            </TableCell>
            <TableCell kind={TableCellKind.Amount} dataQa={getDataQa('total_shows', 'summary_total_shows')}>
                {getFormattedIntNumber(summary.impressions)}
            </TableCell>
            <TableCell kind={TableCellKind.Amount} dataQa={getDataQa('total_clicks', 'summary_total_clicks')}>
                {getFormattedIntNumber(summary.clicks)}
            </TableCell>
            <Fragment>
                {headers.map((source) => (
                    <TableCell
                        key={source}
                        kind={TableCellKind.Amount}
                        dataQa={getDataQa(`${source.toLowerCase()}_clicks`)}
                    >
                        <span className="stat-source-click">{getFormattedIntNumber(data[source] || 0)}</span>
                    </TableCell>
                ))}
            </Fragment>
            <TableCell kind={TableCellKind.Amount} dataQa={getDataQa('total_ctr')}>
                {getCTR(summary.ctr || 0)}
                {' %'}
            </TableCell>
            {showResponsesAndCr && (
                <Fragment>
                    <TableCell kind={TableCellKind.Amount} dataQa={getDataQa('total_response')}>
                        {getFormattedIntNumber(data.RESPONSES || 0)}
                    </TableCell>
                    {showCr && (
                        <TableCell kind={TableCellKind.Amount} dataQa={getDataQa('total_cr')}>
                            {getCTR(summary.cr || 0)}
                            {' %'}
                        </TableCell>
                    )}
                </Fragment>
            )}
            {isAveragePricePerClickAvailable && (
                <TableCell kind={TableCellKind.Amount} dataQa={getDataQa('total_avg_price_per_click')}>
                    {formatCostInCents(summary.averagePricePerClickInCents || 0)}
                    {' ₽'}
                </TableCell>
            )}
            <TableCell kind={TableCellKind.Amount} dataQa={getDataQa('total_amount', 'summary_total_amount')}>
                {getFormattedIntNumber(summary.charges)}
                {' ₽'}
            </TableCell>
        </TableRow>
    );
};

Item.propTypes = {
    isTotal: PropTypes.bool,
    date: PropTypes.string,
    showCr: PropTypes.bool,
    showResponsesAndCr: PropTypes.bool,
    summary: PropTypes.shape({
        charges: PropTypes.number,
        impressions: PropTypes.number,
        clicks: PropTypes.number,
        ctr: PropTypes.number,
        cr: PropTypes.number,
        averagePricePerClickInCents: PropTypes.number,
    }),
    data: PropTypes.shape({
        SOCIAL: PropTypes.number,
        HH: PropTypes.number,
        VK: PropTypes.number,
        ADWORDS: PropTypes.number,
        DIRECT: PropTypes.number,
        FACEBOOK: PropTypes.number,
        INSTAGRAM: PropTypes.number,
        RESPONSES: PropTypes.number,
    }),
    headers: PropTypes.array,
};

export default Item;
